<script>
    import { getContext, createEventDispatcher, onMount } from 'svelte';
    import { Policy, Language } from "@Store";
    import { replace, push } from 'svelte-spa-router';
    import { to } from "@API";
    import translations from "../utils/translations.js";

    const translationMap = {};
    translations.forEach(item => {
        if (item.eng && item.fr) translationMap[item.eng] = item.fr;
    });
    $: t = (text) => ($Language === 'fr' && translationMap[text]) ? translationMap[text] : text;

    const CONTEXT = getContext('CONTEXT');
    const dispatch = createEventDispatcher();

    let loadingPricing = true;
    let pendingEmail = '';
    let pendingLoading = false;
    let showSubmittedModal = false;

    onMount(async () => {
        if ($Policy && !$Policy.valid) {
            push(`/${$Policy.uuid}/applicant`);
            return;
        }

        // Fetch latest policy data for pricing
        if ($Policy?.uuid) {
            let [error, response] = await to(Insurecert.API.policy($Policy.uuid).get());
            if (response) {
                $Policy = { ...response, declaration: true };
            }
        }

        // If the effective date has slipped into the past (e.g. a pending
        // application that was reviewed after the originally selected start
        // date), force it forward to today.
        await bumpEffectiveDateIfPast();

        loadingPricing = false;

        // Prefill email from billing info or named values
        if ($Policy?.billing_info?.email) {
            pendingEmail = $Policy.billing_info.email;
        } else {
            const emailNv = ($Policy?.saved_named_values || []).find(n => n.name === 'email');
            pendingEmail = emailNv?.value?.trim() || '';
        }
    });

    async function bumpEffectiveDateIfPast() {
        const startDateStr = $Policy?.start_date;
        if (!startDateStr) return;

        const todayStr = new Date().toISOString().split('T')[0];
        if (startDateStr >= todayStr) return;

        const endDate = new Date(todayStr);
        endDate.setDate(endDate.getDate() + 365);
        const endDateStr = endDate.toISOString().split('T')[0];

        const effectiveDateQuestion = ($Policy.questions || []).find(q => q.effective_date);
        if (effectiveDateQuestion && CONTEXT?.handleAnswer) {
            CONTEXT.handleAnswer({
                questionId: effectiveDateQuestion.id,
                text_answer: todayStr
            });
        }

        if (CONTEXT?.handleEffectiveDateUpdate) {
            await CONTEXT.handleEffectiveDateUpdate({
                detail: { start_date: todayStr, end_date: endDateStr }
            });
        }
    }

    function checkout() {
        replace(`/${$Policy.uuid}/checkout`);
    }

    async function submitPendingApplication() {
        if (!pendingEmail) return;
        pendingLoading = true;
        try {
            await to(Insurecert.API.policy($Policy.uuid).saveBilling({
                email: pendingEmail,
                name: $Policy.billing_info?.name || '',
                company: $Policy.billing_info?.company || '',
                country: $Policy.billing_info?.country || '',
                country_name: $Policy.billing_info?.country_name || '',
                region: $Policy.billing_info?.region || '',
                region_name: $Policy.billing_info?.region_name || '',
                address: $Policy.billing_info?.address || '',
                postal_code: $Policy.billing_info?.postal_code || '',
                city: $Policy.billing_info?.city || '',
                phone: $Policy.billing_info?.phone || '',
            }));

            await Insurecert.API.policy($Policy.uuid).submitRequest();

            const updated = await Insurecert.API.policy($Policy.uuid).get();
            if (updated) {
                $Policy = updated;
                if ($Policy.status === 'completed') {
                    push(`/${$Policy.uuid}/complete`);
                    return;
                }
            }
            showSubmittedModal = true;
        } catch (e) {
            console.error('Submit application error:', e);
            alert(t('There was an error submitting your application. Please try again.'));
        } finally {
            pendingLoading = false;
        }
    }

    function startNewApplication() {
        const url = new URL(window.location.href);
        url.searchParams.set('new', 'true');
        window.location.href = url.toString();
    }
    
    // Helpers
    function getAnswerObj(q) {
        return $Policy.answers?.[q.id];
    }

    function getChoiceLabel(q, choiceId) {
        if (!choiceId) return '';
        const match = (q.questionchoice_set || []).find(c => String(c.id) === String(choiceId));
        return match ? match.answer : '';
    }

    function isCollectionAnswered(answer) {
        if (!answer || !answer.text_answer) return false;
        try {
            const parsed = JSON.parse(answer.text_answer);
            if (!parsed || !parsed.collections || parsed.collections < 1) return false;
            // any value in first row (or any row)
            const answers = parsed.answers || {};
            for (const uuid of Object.keys(answers)) {
                const arr = answers[uuid] || [];
                for (let i = 0; i < arr.length; i++) {
                    const a = arr[i];
                    if (!a) continue;
                    if ((a.text_answer && a.text_answer.trim() !== '') || a.choice != null || (a.other && a.other.trim() !== '')) {
                        return true;
                    }
                }
            }
            return false;
        } catch (e) {
            return false;
        }
    }

    function getDisplayAnswer(q) {
        const ans = getAnswerObj(q);
        if (!ans) return '';

        if (q.type === 'Collection') {
            return isCollectionAnswered(ans) ? ans : '';
        }

        // Prefer text_answer; fallback to choice label; fallback to other
        const text = (ans.text_answer || '').trim();
        if (text) return text;

        if (ans.choice != null) {
            const label = getChoiceLabel(q, ans.choice);
            if (label) return label;
        }

        const other = (ans.other || '').trim();
        if (other) return other;

        return '';
    }

    // Build a list of answered questions for display (skip blank)
    $: answeredList = ($Policy.questions || [])
        .map(q => ({ question: q, display: getDisplayAnswer(q) }))
        .filter(item => {
            if (!item.display) return false;
            // Collections pass the full answer object back
            if (item.question.type === 'Collection') return !!item.display;
            return String(item.display).trim() !== '';
        });

    // Parse collection data for rendering
    function parseCollection(answer) {
        try {
            return JSON.parse(answer.text_answer || '{}');
        } catch (_) {
            return { collections: 0, answers: {} };
        }
    }

    function formatDate(dateString) {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return dateString;
        const locale = $Language === 'fr' ? 'fr-CA' : 'en-US';
        return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
    }

    function formatCurrency(amount) {
        if (amount == null || isNaN(amount)) return '$0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(parseFloat(amount));
    }

    function printQuote() {
        window.print();
    }

    function formatCompact(value) {
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        if (num >= 1000000) {
            return (num / 1000000).toLocaleString('en-US') + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toLocaleString('en-US') + 'k';
        }
        return num.toLocaleString('en-US');
    }

    $: namedValuesMap = ($Policy.saved_named_values || []).reduce((map, nv) => {
        map[nv.name] = nv.value?.trim() || '';
        return map;
    }, {});

    $: showPricing = $Policy.status && $Policy.status !== 'pending';
    $: premium = $Policy.premium || 0;
    $: serviceFee = parseFloat($Policy.service_fee || 0);
    $: subtotal = $Policy.total || 0;
    $: isPending = ($Policy.status === 'pending');
    $: pendingReason = namedValuesMap['pending_reason'] || '';

    $: medmal_limit_selected = namedValuesMap['medmal_limit_selected'] || '';
    $: clinic_medmal_premium = namedValuesMap['clinic_medmal_premium'] || '';

    $: cgl_limit_selected = namedValuesMap['cgl_limit_selected'] || '';
    $: cglLimitNum = parseFloat(String(cgl_limit_selected).replace(/[^0-9.-]/g, '')) || 0;
    $: cglMinRetainedPercent = cglLimitNum === 2000000 ? 100 : 50;
    $: abuse_limit_swg = namedValuesMap['Abuse Limit - SWG'] || '';
    $: abuse_premium_swg = namedValuesMap['Abuse Premium - SWG'] || '';

    // $: cgl_only_selected = namedValuesMap['medmal_limit_selected'] || '';
    $: cgl_premium = namedValuesMap['cgl_premium'] || '';
    $: taxTotal = parseFloat($Policy.taxtotal || 0); 

    $: has_med_mal_swg = namedValuesMap['has_med_mal_swg'] || '';
    $: cgl_only_selected = namedValuesMap['cgl_only_selected'] || '';
    $: has_swg = has_med_mal_swg || cgl_only_selected;

    $: wawa_cgl_limit = namedValuesMap['wawa_cgl_limit'] || '';
    $: wawa_cgl_limit_selected = namedValuesMap['wawa_cgl_limit_selected'] || '';

    $: property_limit = namedValuesMap['property_limit_selected'] || '';
    $: property = namedValuesMap['property_premium'] || '';

    $: has_office_package_wawa = namedValuesMap['office_package_wawa'] || '';
    $: has_office_package_wawa_solo = namedValuesMap['office_package_wawa_solo'] || '';

    $: crime_limit_premium = namedValuesMap['crime_limit_premium'] || '';
    $: additional_locations_count = parseInt(namedValuesMap['additional_locations_count'] || 0) || 0;
    $: additional_locations_premium = namedValuesMap['additional_locations_premium'] || '';

</script>

<div class="summary-page">
    <div class="summary-header">
        <h1>{t("Your Application Summary")}</h1>
        <p class="sub">{t("Review your answers below. Expand any section to view details.")}</p>
    </div>

    <div class="accordion">
        <details class="panel" closed>
            <summary class="panel-header">
                <div class="panel-title">
                    <span class="badge">{answeredList.length}</span>
                    {t("Answers")}
                </div>
                <svg class="chev" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M8 10l4 4 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </summary>
            <div class="panel-body">
                {#if answeredList.length === 0}
                    <div class="empty">{t("No answers yet.")}</div>
                {:else}
                    <ul class="answer-list">
                        {#each answeredList as item (item.question.id)}
                            <li class="answer-item">
                                <details class="qa">
                                    <summary class="qa-header">
                                        <span class="q">{t(item.question.question)}</span>
                                        <span class="status ok">
                                            <svg viewBox="0 0 24 24" class="ok-icon" fill="none" aria-hidden="true"><path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                        </span>
                                    </summary>

                                    <div class="qa-body">
                                        {#if item.question.type === 'Collection'}
                                            {#key item.question.id}
                                                {#await Promise.resolve(parseCollection(item.display)) then col}
                                                    {#if col && col.collections > 0}
                                                        <div class="collection-summary">
                                                            {#each Array(col.collections) as _, idx}
                                                                <div class="collection-card">
                                                                    <div class="collection-card-header">
                                                                        <div class="title">{t("Claim")} {idx + 1}</div>
                                                                    </div>
                                                                    <div class="collection-grid">
                                                                        {#each (item.question.inner_questions || []).sort((a,b)=>a.position-b.position) as iq (iq.uuid)}
                                                                            {#if col.answers?.[iq.uuid]?.[idx]}
                                                                                {#if iq.type && iq.type.startsWith('Calendar')}
                                                                                    <div class="cell">
                                                                                        <div class="label">{t(iq.question_text)}</div>
                                                                                        <div class="value">{formatDate(col.answers[iq.uuid][idx].text_answer)}</div>
                                                                                    </div>
                                                                                {:else}
                                                                                    <div class="cell">
                                                                                        <div class="label">{t(iq.question_text)}</div>
                                                                                        <div class="value">{t(col.answers[iq.uuid][idx].text_answer)}</div>
                                                                                    </div>
                                                                                {/if}
                                                                            {/if}
                                                                        {/each}
                                                                    </div>
                                                                </div>
                                                            {/each}
                                                        </div>
                                                    {:else}
                                                        <div class="muted">{t("No entries provided.")}</div>
                                                    {/if}
                                                {/await}
                                            {/key}
                                        {:else}
                                            <div class="a">{t(item.display)}</div>
                                        {/if}
                                    </div>
                                </details>
                            </li>
                        {/each}
                    </ul>
                {/if}
            </div>
        </details>
    </div>

    {#if showPricing || loadingPricing}
        <div class="pricing-summary" id="pricing-to-print">
            <div class="pricing-card">
                <div class="pricing-header">
                    <h2>{t("Policy Pricing")}</h2>
                </div>
                {#if loadingPricing}
                    <div class="pricing-body pricing-loading">
                        <div class="spinner"></div>
                        <span class="loading-text">{t("Calculating your quote...")}</span>
                    </div>
                {:else}
                <div class="pricing-body">

                        <div class="pricing-row column-headers">
                            <span class="pricing-label"></span>
                            <span class="pricing-middle">{t("Limit")}</span>
                            <span class="pricing-value">{t("Premium")}</span>
                        </div>
                        <!-- CLINIC MED MAL - SWG -->
                        <div class="pricing-section">
                            <div class="section-header">{t("South Western Group")}<span class="not-selected">{has_swg ? '' : t('Not Selected')} </span></div>

                            {#if medmal_limit_selected}
                                <div class="pricing-row no-line">
                                    <span class="pricing-label">{t("Clinic Medical Malpractice")}</span>
                                    <span class="pricing-middle">{formatCurrency(medmal_limit_selected)}</span>
                                    <span class="pricing-value">{formatCurrency(clinic_medmal_premium)}</span>
                                </div>
                                <div class="pricing-row no-line" style="padding-left: 40px;">
                                    <span class="pricing-label">{t("Minimum Retained Premium")}</span>
                                    <span class="pricing-middle">50%</span>
                                    <span class="pricing-value"></span>
                                </div>
                            {/if}

                            {#if abuse_premium_swg > 0 || abuse_premium_swg === 'Included'}
                                <div class="pricing-row no-line">
                                    <span class="pricing-label">{t("Abuse Limit")}</span>
                                    <span class="pricing-middle">{formatCurrency(abuse_limit_swg)}</span>
                                    <span class="pricing-value">{abuse_premium_swg === 'Included' ? t('Included') : formatCurrency(abuse_premium_swg)}</span>
                                </div>
                            {/if}

                            {#if cgl_premium > 0}
                                <div class="pricing-row no-line">
                                    <span class="pricing-label">{t("Commercial General Liability")}</span>
                                    <span class="pricing-middle">{formatCurrency(cgl_limit_selected)}</span>
                                    <span class="pricing-value">{formatCurrency(cgl_premium)}</span>
                                </div>
                                <div class="pricing-row no-line" style="padding-left: 40px;">
                                    <span class="pricing-label">{t("Minimum Retained Premium")}</span>
                                    <span class="pricing-middle">{cglMinRetainedPercent}%</span>
                                    <span class="pricing-value"></span>
                                </div>
                            {/if}


                        </div>

                        <div class="pricing-section">
                            <div class="section-header">{t("Wawanesa")}<span class="not-selected">{has_office_package_wawa || has_office_package_wawa_solo ? '' : t('Not Selected')} </span></div>

                            {#if wawa_cgl_limit}
                                <div class="pricing-row no-line">
                                    <span class="pricing-label">{t("Commercial General Liability")}</span>
                                    <span class="pricing-middle">{formatCurrency(wawa_cgl_limit_selected)}</span>
                                    <span class="pricing-value">{formatCurrency(wawa_cgl_limit)}</span>
                                </div>
                            {/if}

                            {#if property_limit > 0 && property > 0}
                                <div class="pricing-row no-line" style="padding-left: 40px;">
                                    <span class="pricing-label">{t("Property")}</span>
                                    <span class="pricing-middle">{formatCurrency(property_limit)}</span>
                                    <span class="pricing-value">{formatCurrency(property)}</span>
                                </div>
                                <div class="pricing-row no-line" style="padding-left: 40px;">
                                    <span class="pricing-label">{t("Business Interruption")}</span>
                                    <span class="pricing-middle">{t("Included")}</span>
                                    <span class="pricing-value"></span>
                                </div>
                                {#if crime_limit_premium > 0}
                                    <div class="pricing-row no-line"  style="padding-left: 40px;">
                                        <span class="pricing-label">{t("Crime Limit")}</span>
                                        <span class="pricing-middle">{'$25,000'}</span>
                                        <span class="pricing-value">{formatCurrency(crime_limit_premium)}</span>
                                    </div>
                                {/if}
                                {#if additional_locations_count > 0}
                                    <div class="pricing-row no-line"  style="padding-left: 40px;">
                                        <span class="pricing-label">{t("Additional locations")} ({additional_locations_count})</span>
                                        <span class="pricing-middle"></span>
                                        <span class="pricing-value">{formatCurrency(additional_locations_premium || 0)}</span>
                                    </div>
                                {/if}
                            {/if}

                            {#if has_office_package_wawa === 'true'}
                                <div class="pricing-row no-line" style="padding-left: 40px;">
                                    <span class="pricing-label">{t("Equipment Breakdown")}</span>
                                    <span class="pricing-middle">{t("Included")}</span>
                                    <span class="pricing-value"></span>
                                </div>
                                <div class="pricing-row no-line" style="padding-left: 40px;">
                                    <span class="pricing-label">{t("Cyber")}</span>
                                    <span class="pricing-middle">{'$50,000'}</span>
                                    <span class="pricing-value">{t("Included")}</span>
                                </div>
                                <div class="pricing-row no-line" style="padding-left: 40px;">
                                    <span class="pricing-label">{t("Legal Expense")}</span>
                                    <span class="pricing-middle">{'$100,000 ($500,000 aggregate)'}</span>
                                    <span class="pricing-value">{t("")}</span>
                                </div>
                                <div class="pricing-row no-line" style="padding-left: 80px;">
                                    <span class="pricing-label">{t("24 Hour Unlimited Legal Helpline")}</span>
                                    <span class="pricing-middle">{t("Included")}</span>
                                    <span class="pricing-value"></span>
                                </div>
                            {/if}

                            {#if has_office_package_wawa_solo === 'true'}
                                <div class="pricing-row no-line" style="padding-left: 40px;">
                                    <span class="pricing-label">{t("Equipment Breakdown")}</span>
                                    <span class="pricing-middle">{t("Not Covered")}</span>
                                    <span class="pricing-value"></span>
                                </div>
                                <div class="pricing-row no-line" style="padding-left: 40px;">
                                    <span class="pricing-label">{t("Cyber")}</span>
                                    <span class="pricing-middle">{t("Not Covered")}</span>
                                    <span class="pricing-value"></span>
                                </div>
                                <div class="pricing-row no-line" style="padding-left: 40px;">
                                    <span class="pricing-label">{t("Legal Expense")}</span>
                                    <span class="pricing-middle">{t("Not Covered")}</span>
                                    <span class="pricing-value"></span>
                                </div>
                            {/if}
                        </div>



                    <div class="pricing-row">
                        <span class="pricing-label">{t("Premium")}</span>
                        <span class="pricing-middle"></span>
                        <span class="pricing-value">{premium}</span>
                    </div>
                    <div class="pricing-row">
                        <span class="pricing-label">{t("Service Fee (3.2%)")}</span>
                        <span class="pricing-middle"></span>
                        <span class="pricing-value">{formatCurrency(serviceFee)}</span>
                    </div>
                    <div class="pricing-row">
                        <span class="pricing-label">{t("Provincial Tax")}</span>
                        <span class="pricing-middle"></span>
                        <span class="pricing-value">{formatCurrency(taxTotal)}</span>
                    </div>
                    <div class="pricing-row total">
                        <span class="pricing-label">{t("Total")}</span>
                        <span class="pricing-middle"></span>
                        <span class="pricing-value">{formatCurrency(subtotal)}</span>
                    </div>
                </div>
                {/if}
            </div>

            {#if !loadingPricing}
                <p class="pricing-disclaimer">
                    {t($Language === 'fr'
                        ? "Les estimations de primes et les limites de couverture décrites dans la proposition ci-dessus sont basées sur l'exactitude des informations que vous avez fournies et peuvent ne pas représenter toutes les couvertures disponibles. Cette proposition ne constitue pas un contrat ou un engagement d'assurance et les montants des primes ne peuvent être garantis tant que la couverture n'est pas achetée."
                        : 'The premium estimates and coverage limits outlined in the proposal above are based upon the accuracy of the information you provided and may not represent all coverages available. This proposal does not constitute a contract or binder of insurance and premium amounts cannot be guaranteed until coverage is purchased.')}
                </p>

                <div class="print-button-container" id="print-quote-button">
                    <button class="print-button" on:click={printQuote}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <rect x="6" y="14" width="12" height="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        {t($Language === 'fr' ? 'Imprimer le devis' : 'Print Quote')}
                    </button>
                </div>
            {/if}
        </div>
    {/if}

    {#if isPending}
        <div class="pending-alert">
            <div class="pending-card">
                <div class="pending-header">
                    <h2>{t('Your application requires additional review')}</h2>
                </div>
                <div class="pending-body">
                    <p>
                        {t('We were unable to generate an instant quote for your application. Submit your email below and our team will review your application and follow up with a personalized quote.')}
                    </p>
                </div>
            </div>
        </div>
    {/if}

    <div class="attestation">
        <div class="attestation-card">
            <p class="attestation-text">{t($Language === 'fr' ? "Je confirme et accepte que:" : 'I confirm and agree that:')}</p>
            <ul class="attestation-list">
                <li>{t($Language === 'fr' ? "Je suis un membre actuel de la Canadian Chiropractic Protective Association (ACCP) en bonne standing et je comprends que mon admissibilite a la couverture sous ce programme d'assurance est conditionnelle a ma continuite de membre en bonne standing avec l'ACCP." : 'I am a current member of the Canadian Chiropractic Protective Association (CCPA) in good standing and I understand that my eligibility for coverage under this insurance program is conditional upon my continued membership in good standing with the CCPA.')}</li>
                <li>{t($Language === 'fr' ? "Toutes les informations que j'ai fournies en relation avec cette demande, y compris toutes les informations ou documents pris en charge, sont vraies, completes et precises au mieux de ma connaissance. Je comprends que ces informations seront utilisees par l'assureur et l'administrateur du programme (Insurance Program Managers Groups & Associations) pour determiner mon admissibilite a la couverture, les termes de la couverture et le montant de la prime." : 'All information I have provided in connection with this application, including any supporting information or documents, is true, complete, and accurate to the best of my knowledge. I understand that this information will be relied upon by the insurer and the program administrator (being Insurance Program Managers Groups & Associations) in determining my eligibility for coverage, the terms of coverage, and the premium charged.')}</li>
                <li>{t($Language === 'fr' ? "Je reconnais et accepte que toute fausse declaration, omission ou declaration erronee, quelle qu'en soit la cause, peut entrainer le refus ou la resiliation de la couverture, ou le refus de payer un sinistre, conformement a la legislation applicable a l'assurance." : 'I acknowledge and agree that any misrepresentation, omission, or false statement, whether intentional or unintentional, may result in the denial or cancellation of coverage, or denial of a claim, in accordance with applicable insurance legislation.')}</li>
                <li>{t($Language === 'fr' ? "J'autorise l'administrateur du programme et l'assureur a collecter, utiliser et divulguer mes informations personnelles a des fins d'evaluation, d'administration de la police et de gestion des sinistres, conformement a la loi." : 'I authorize the program administrator and insurer to collect, use, and disclose my personal information for the purposes of underwriting, policy administration, and claims handling, as permitted by law.')}</li>
                <li>{t($Language === 'fr' ? "Je reconnais et accepte que l'administrateur du programme et l'assureur peuvent partager mes informations personnelles avec l'ACCP, a des fins de verification de mon statut de membre, et je consent a de telles divulgations." : 'I acknowledge and agree that the program administrator and insurer may periodically share personal information with the CCPA, for the purpose of verifying my membership status, and I hereby consent to such disclosure.')}</li>
            </ul>
            <p class="attestation-footer">{t($Language === 'fr' ? "En continuant, je confirme que j'ai lu, compris et accepte les termes ci-dessus." : 'By proceeding, I confirm that I have read, understood, and agree to the above.')}</p>
        </div>
    </div>

    {#if isPending}
        <div class="get-quote-section">
            <div class="get-quote-card">
                <h2 class="get-quote-title">{t('Get My Quote')}</h2>
                <p class="get-quote-desc">{t("Enter your email and we'll send you a personalized quote.")}</p>
                <div class="get-quote-form">
                    <input
                        type="email"
                        class="get-quote-input"
                        placeholder={t('Your email address')}
                        bind:value={pendingEmail}
                    />
                    <button
                        class="get-quote-btn"
                        on:click={submitPendingApplication}
                        disabled={!pendingEmail || pendingLoading}
                    >
                        {#if pendingLoading}
                            <span class="btn-spinner"></span>
                        {/if}
                        {pendingLoading ? t('Submitting...') : t('Get My Quote')}
                    </button>
                </div>
            </div>
        </div>
    {:else}
        <div class="actions">
            <button class="primary" on:click={() => checkout()}>
                {t($Language === 'fr' ? 'Continuer vers le paiement' : 'Proceed to Checkout')}
            </button>
        </div>
    {/if}
</div>

{#if showSubmittedModal}
<div class="modal-backdrop" role="dialog" aria-modal="true">
    <div class="modal-card">
        <div class="modal-header-content">
            <div class="icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M7 12l3 3 7-7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
            <h3>{t('Application Submitted')}</h3>
            <p>{t("Your application has been submitted for review. We'll email you at")} <strong>{pendingEmail}</strong> {t('with your personalized quote.')}</p>
        </div>
        <div class="modal-actions">
            <button class="modal-primary" on:click={startNewApplication}>{t('Start New Application')}</button>
        </div>
    </div>
</div>
{/if}

<style>
    .summary-page {
        max-width: 820px;
        margin: 0 auto;
        padding: 2rem 1rem 3rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .summary-header {
        text-align: center;
        margin-bottom: 1.5rem;
    }

    .summary-header h1 {
        margin: 0 0 0.5rem 0;
        font-size: 1.75rem;
        letter-spacing: -0.02em;
        color: #1d1d1f;
        font-weight: 700;
    }

    .summary-header .sub {
        color: #666;
        margin: 0;
    }

    /* Accordion */
    .accordion {
        width: 100%;
        margin: 1rem 0 2rem;
    }

    .panel {
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 4px 20px rgba(0,0,0,0.04);
    }

    .panel-header {
        list-style: none;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1.25rem 1.25rem;
        cursor: pointer;
        user-select: none;
    }

    .panel[open] .panel-header {
        border-bottom: 2px solid #e9ecef;
    }

    .panel-title {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        font-weight: 600;
        color: #1d1d1f;
        letter-spacing: -0.01em;
        font-size: 1.1rem;
    }

    .badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 999px;
        background: var(--gold);
        color: #fff;
        font-size: 0.875rem;
        font-weight: 700;
        box-shadow: 0 2px 10px rgba(0,113,227,0.25);
    }

    .chev {
        width: 20px;
        height: 20px;
        color: #1d1d1f;
        transition: transform 0.2s ease;
    }

    .panel[open] .chev {
        transform: rotate(180deg);
    }

    .panel-body {
        padding: 1.25rem;
        background: #fff;
    }

    .empty {
        color: #86868b;
        text-align: center;
        padding: 1rem;
    }

    /* Answers list */
    .answer-list {
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    .answer-item {
        background: linear-gradient(135deg, #ffffff 0%, #fbfbfc 100%);
        border: 2px solid #edf0f2;
        border-radius: 16px;
        overflow: hidden;
    }

    .qa {
        margin: 0;
    }

    .qa-header {
        list-style: none;
        display: flex;
        gap: 0.75rem;
        align-items: center;
        justify-content: space-between;
        padding: 1rem 1rem;
        cursor: pointer;
        user-select: none;
    }

    .qa[open] .qa-header {
        border-bottom: 2px solid #edf0f2;
    }

    .q {
        color: #1d1d1f;
        font-weight: 600;
        letter-spacing: -0.01em;
        text-align: left;
        flex: 1;
        margin-right: 0.75rem;
    }

    .status.ok {
        color: #0f9d58;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 999px;
        background: rgba(15,157,88,0.12);
        border: 2px solid rgba(15,157,88,0.25);
    }

    .ok-icon {
        width: 18px;
        height: 18px;
    }

    .qa-body {
        padding: 1rem 1rem 1.25rem;
        background: #fff;
    }

    .a {
        color: #1d1d1f;
        font-weight: 500;
        letter-spacing: -0.01em;
        word-break: break-word;
    }

    /* Collection summary cards */
    .collection-summary {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1rem;
    }

    .collection-card {
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 14px;
        box-shadow: 0 2px 14px rgba(0,0,0,0.04);
        overflow: hidden;
    }

    .collection-card-header {
        padding: 0.75rem 1rem;
        border-bottom: 2px solid #e9ecef;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .collection-card-header .title {
        font-weight: 600;
        color: #1d1d1f;
        letter-spacing: -0.01em;
    }

    .collection-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.75rem;
        padding: 0.75rem 1rem 1rem;
    }

    .cell {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        background: #fff;
        border: 1px solid #edf0f2;
        border-radius: 10px;
        padding: 0.75rem;
    }

    .label {
        font-size: 0.85rem;
        color: #6b7280;
        font-weight: 600;
        letter-spacing: -0.01em;
    }

    .value {
        font-size: 1rem;
        color: #1d1d1f;
        font-weight: 500;
        letter-spacing: -0.01em;
        word-break: break-word;
    }

    /* Attestation */
    .attestation {
        margin-top: 1.5rem;
    }

    .attestation-card {
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        padding: 1.5rem;
        box-shadow: 0 4px 20px rgba(0,0,0,0.04);
    }

    .attestation-title {
        margin: 0 0 0.75rem 0;
        font-size: 1.1rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.02em;
    }

    .attestation-text {
        color: #1d1d1f;
        opacity: 0.85;
        line-height: 1.7;
        margin: 0 0 0.75rem 0;
        font-size: 0.95rem;
    }

    .attestation-list {
        margin: 0;
        padding: 0 0 0 1.25rem;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    .attestation-list li {
        color: #1d1d1f;
        opacity: 0.85;
        line-height: 1.7;
        font-size: 0.9rem;
    }

    .attestation-footer {
        margin: 1rem 0 0 0;
        font-weight: 600;
        color: #1d1d1f;
        font-size: 0.95rem;
        line-height: 1.5;
    }

    /* Actions */
    .actions {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-top: 1.25rem;
    }

    .primary {
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border: 2px solid var(--base-green);
        border-radius: 14px;
        padding: 0.875rem 1.5rem;
        cursor: pointer;
        font-size: 1rem;
        font-weight: 700;
        color: #fff;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        letter-spacing: -0.01em;
        box-shadow: 0 6px 20px rgba(49, 104, 50, 0.25);
    }

    .primary:hover:not([disabled]) {
        transform: translateY(-2px);
        box-shadow: 0 10px 28px rgba(49, 104, 50, 0.35);
    }

    .primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        box-shadow: none;
    }

    /* Pricing Summary */
    .pricing-summary {
        margin-top: 1.5rem;
    }

    .pricing-card {
        background: linear-gradient(135deg, #fffbf0 0%, #ffffff 100%);
        border: 2px solid #f5d99f;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 4px 20px rgba(227, 161, 37, 0.12);
    }

    .pricing-header {
        padding: 1.25rem 1.5rem;
        border-bottom: 2px solid #f5d99f;
        background: linear-gradient(135deg, #fef3dc 0%, #fffcf5 100%);
    }

    .pricing-header h2 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.02em;
    }

    .pricing-body {
        padding: 1.5rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .pricing-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.75rem 0;
        border-bottom: 1px solid #f5e5c5;
    }

    .pricing-row:last-child {
        border-bottom: none;
    }

    .pricing-row.no-line {
        border-bottom: none;
    }

    .pricing-section {
        margin-bottom: 1.25rem;
    }

    .pricing-section:last-of-type {
        margin-bottom: 0;
        padding-bottom: 1rem;
        border-bottom: 2px solid #f5d99f;
    }

    .section-header {
        font-size: 1.0625rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.01em;
        margin-bottom: 0.75rem;
        padding-bottom: 0.5rem;
        border-bottom: 2px solid rgba(245, 217, 159, 0.5);
    }

    .pricing-section .pricing-row {
        padding-left: 1rem;
    }

    .pricing-row.total {
        margin-top: 0.5rem;
        padding-top: 1rem;
        border-top: 2px solid var(--gold);
        border-bottom: none;
    }

    .pricing-label {
        font-size: 1rem;
        color: #6b7280;
        font-weight: 600;
        letter-spacing: -0.01em;
    }

    .pricing-row.total .pricing-label {
        font-size: 1.125rem;
        color: #1d1d1f;
        font-weight: 700;
    }

    .pricing-value {
        font-size: 1rem;
        color: #1d1d1f;
        font-weight: 600;
        letter-spacing: -0.01em;
    }

    .pricing-row.total .pricing-value {
        font-size: 1.375rem;
        color: var(--gold);
        font-weight: 700;
    }

    .pending-alert {
        margin-top: 1.5rem;
    }
    .pending-card {
        background: linear-gradient(135deg, #fff5f5 0%, #ffffff 100%);
        border: 2px solid #f5c2c2;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 4px 20px rgba(220, 53, 69, 0.1);
    }
    .pending-header {
        padding: 1.25rem 1.5rem;
        border-bottom: 2px solid #f5c2c2;
        background: linear-gradient(135deg, #ffe3e3 0%, #fff8f8 100%);
    }
    .pending-header h2 {
        margin: 0;
        font-size: 1.2rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.02em;
    }
    .pending-body {
        padding: 1.25rem 1.5rem;
        color: #1d1d1f;
    }

    @media (min-width: 640px) {
        .collection-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .pricing-body {
            padding: 2rem;
        }

        .pricing-label {
            font-size: 1.0625rem;
        }

        .pricing-value {
            font-size: 1.125rem;
        }

        .pricing-row.total .pricing-label {
            font-size: 1.25rem;
        }

        .pricing-row.total .pricing-value {
            font-size: 1.5rem;
        }
    }

    @media (min-width: 900px) {
        .collection-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }
    }


.not-selected {
    color: #86868b;
    font-size: 0.875rem;
    font-weight: 500;
    letter-spacing: -0.01em;
}

.column-headers {
    font-weight: 700;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    /* border-bottom: 2px solid #e5e7eb; */
    border-bottom: none;
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
    padding-left: 1rem;
}

.column-headers .pricing-label {
    color: #9ca3af;
    font-size: 0.75rem;
    font-weight: 700;
}

.column-headers .pricing-middle {
    color: #9ca3af;
    font-size: 0.75rem;
    font-weight: 700;
}

.column-headers .pricing-value {
    color: #9ca3af;
    font-size: 0.75rem;
    font-weight: 700;
}

.section-header {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;  /* Adjust these ratios as needed */
    gap: 1rem;
    align-items: center;
}

.pricing-disclaimer {
    font-size: 0.75rem;
    color: #6b7280;
    line-height: 1.5;
    margin-top: 1rem;
    padding: 0.75rem 1rem;
    background: #f9fafb;
    border-left: 3px solid #d1d5db;
    border-radius: 0 4px 4px 0;
    font-style: italic;
}

.pricing-header h2 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.02em;
    }

    .pricing-body {
        padding: 1.5rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .pricing-row {
        display: grid;
        grid-template-columns: 2fr 1fr 1fr;  /* Adjust these ratios as needed */
        gap: 1rem;
        align-items: center;
        padding: 0.75rem 0;
        border-bottom: 1px solid #f5e5c5;
    }

    .pricing-label {
        font-size: 1rem;
        color: #6b7280;
        font-weight: 600;
        letter-spacing: -0.01em;
    }

    .pricing-middle {
        font-size: 0.875rem;
        color: #86868b;
        font-weight: 500;
        text-align: left;
        white-space: nowrap;
    }

    .pricing-value {
        font-size: 1rem;
        color: #1d1d1f;
        font-weight: 600;
        letter-spacing: -0.01em;
        text-align: right;
    }

    .pricing-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem 1.5rem;
        gap: 1rem;
    }

    .spinner {
        width: 40px;
        height: 40px;
        border: 3px solid #f5e5c5;
        border-top-color: var(--gold);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }

    .loading-text {
        color: #6b7280;
        font-size: 0.95rem;
        font-weight: 500;
    }

    /* Get My Quote Section */
    .get-quote-section {
        margin-top: 1.5rem;
    }

    .get-quote-card {
        background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
        border: 2px solid var(--base-green);
        border-radius: 20px;
        padding: 1.5rem;
        box-shadow: 0 4px 20px rgba(49, 104, 50, 0.1);
    }

    .get-quote-title {
        margin: 0 0 0.5rem 0;
        font-size: 1.25rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.02em;
    }

    .get-quote-desc {
        margin: 0 0 1.25rem 0;
        color: #4b5563;
        font-size: 0.95rem;
        line-height: 1.5;
    }

    .get-quote-form {
        display: flex;
        gap: 0.75rem;
        align-items: stretch;
    }

    .get-quote-input {
        flex: 1;
        padding: 0.875rem 1rem;
        font-size: 1rem;
        border: 2px solid #e9ecef;
        border-radius: 12px;
        background: #fff;
        color: #1d1d1f;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        transition: all 0.2s ease;
        box-sizing: border-box;
    }

    .get-quote-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 3px rgba(49, 104, 50, 0.1);
    }

    .get-quote-btn {
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border: 2px solid var(--base-green);
        border-radius: 14px;
        padding: 0.875rem 1.5rem;
        cursor: pointer;
        font-size: 1rem;
        font-weight: 700;
        color: #fff;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        letter-spacing: -0.01em;
        box-shadow: 0 6px 20px rgba(49, 104, 50, 0.25);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        white-space: nowrap;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .get-quote-btn:hover:not([disabled]) {
        transform: translateY(-2px);
        box-shadow: 0 10px 28px rgba(49, 104, 50, 0.35);
    }

    .get-quote-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        box-shadow: none;
    }

    .btn-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: #fff;
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
    }

    @media (max-width: 480px) {
        .get-quote-form {
            flex-direction: column;
        }
    }

    /* Print Button */
    .print-button-container {
        margin-top: 1rem;
        display: flex;
        justify-content: center;
    }

    .print-button {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border: 2px solid var(--base-green);
        border-radius: 14px;
        padding: 0.875rem 1.5rem;
        cursor: pointer;
        font-size: 1rem;
        font-weight: 700;
        color: #fff;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        letter-spacing: -0.01em;
        box-shadow: 0 6px 20px rgba(49, 104, 50, 0.25);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .print-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 28px rgba(49, 104, 50, 0.35);
    }

    .print-button svg {
        flex-shrink: 0;
    }

    /* Print Media Queries */
    @media print {
        :global(body *) {
            visibility: hidden !important;
        }

        :global(#pricing-to-print),
        :global(#pricing-to-print *) {
            visibility: visible !important;
        }

        :global(#pricing-to-print) {
            position: absolute;
            inset: 0;
            margin: 0;
            width: 100%;
            box-shadow: none;
            border: none;
            background: #fff;
        }

        :global(#pricing-to-print .pricing-card) {
            border: none;
            box-shadow: none;
        }

        :global(#pricing-to-print .pricing-row) {
            border-color: #bbb;
        }

        :global(#pricing-to-print .pricing-row.total) {
            border-top: 2px solid #000;
        }

        :global(#print-quote-button) {
            display: none !important;
        }
    }

    /* Modal */
    .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(17, 24, 39, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 50;
        padding: 1rem;
    }

    .modal-card {
        max-width: 540px;
        width: 100%;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
        padding: 1.5rem;
    }

    .modal-header-content {
        text-align: center;
    }

    .icon-wrap {
        width: 52px;
        height: 52px;
        border-radius: 999px;
        margin: 0 auto 0.75rem;
        background: rgba(49, 104, 50, 0.12);
        color: var(--base-green);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .icon-wrap svg {
        width: 26px;
        height: 26px;
    }

    .modal-header-content h3 {
        margin: 0.25rem 0 0.25rem 0;
        font-size: 1.25rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.02em;
    }

    .modal-header-content p {
        margin: 0;
        color: #4b5563;
        font-size: 0.975rem;
        line-height: 1.5;
    }

    .modal-actions {
        margin-top: 1.25rem;
        display: flex;
        gap: 0.75rem;
        justify-content: center;
    }

    .modal-primary {
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border: 2px solid var(--base-green);
        border-radius: 12px;
        padding: 0.75rem 1.25rem;
        color: #fff;
        font-weight: 700;
        cursor: pointer;
        box-shadow: 0 6px 20px rgba(49, 104, 50, 0.25);
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .modal-primary:hover {
        transform: translateY(-1px);
        box-shadow: 0 10px 28px rgba(49, 104, 50, 0.35);
    }

</style>